import { useState } from 'react';
import { Plus, KeyRound, Copy, Check } from 'lucide-react';
import { api } from '../api';
import { useLoad } from '../hooks/useLoad';
import { Card, Badge, EmptyState } from '../components/ui';

interface KeyRow {
  id: string;
  prefix: string;
  label: string;
  revoked_at: string | null;
  created_at: string;
}
const PREFIXES = ['sk_live_', 'sk_test_', 'pk_live_', 'pk_test_'];

// API key management — rendered as a tab inside the API / MCP page (no own PageHeader).
export function ApiKeysSection() {
  const { data, error, reload } = useLoad(() => api.get<{ data: KeyRow[] }>('/v1/api-keys'));
  const [label, setLabel] = useState('');
  const [prefix, setPrefix] = useState(PREFIXES[0]!);
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try {
      const res = await api.post<{ plaintext: string }>('/v1/api-keys', { label, prefix });
      setPlaintext(res.plaintext);
      setCopied(false);
      setLabel('');
      reload();
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    await api.del(`/v1/api-keys/${id}`);
    reload();
  }

  function copy() {
    if (plaintext) void navigator.clipboard?.writeText(plaintext).then(() => setCopied(true));
  }

  const keys = data?.data ?? [];
  return (
    <div>
      {error && (
        <div className="mb-4 rounded-lg border border-line bg-ink/[0.03] px-3 py-2 text-sm text-muted">
          {error}
        </div>
      )}

      {plaintext && (
        <div className="mb-4 rounded-xl border border-ink/20 bg-ink/[0.03] p-4">
          <div className="text-sm font-semibold text-ink">
            Copy this secret now — it won’t be shown again.
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 break-all rounded-lg bg-white/70 px-3 py-2 font-mono text-sm text-ink">
              {plaintext}
            </code>
            <button onClick={copy} className="btn-outline shrink-0">
              {copied ? <Check className="h-4 w-4 text-ink" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button onClick={() => setPlaintext(null)} className="mt-2 text-xs text-muted underline">
            Dismiss
          </button>
        </div>
      )}

      <Card className="mb-4 flex flex-wrap items-end gap-3 p-5">
        <label className="min-w-[12rem] flex-1">
          <span className="mb-1 block text-xs font-medium text-slate-500">Label</span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="input"
            placeholder="CI pipeline"
          />
        </label>
        <label>
          <span className="mb-1 block text-xs font-medium text-slate-500">Type</span>
          <select
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            className="input w-36 cursor-pointer"
          >
            {PREFIXES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <button onClick={() => void create()} disabled={busy || !label} className="btn-primary">
          <Plus className="h-4 w-4" /> Create key
        </button>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50/60 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-5 py-3">Label</th>
              <th className="px-5 py-3">Prefix</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {keys.map((k) => (
              <tr key={k.id} className="transition-colors hover:bg-slate-50/60">
                <td className="px-5 py-3 font-medium text-slate-800">{k.label}</td>
                <td className="px-5 py-3 font-mono text-xs text-slate-500">{k.prefix}</td>
                <td className="px-5 py-3">
                  <Badge tone={k.revoked_at ? 'muted' : 'outline'}>
                    {k.revoked_at ? 'revoked' : 'active'}
                  </Badge>
                </td>
                <td className="px-5 py-3 text-right">
                  {!k.revoked_at && (
                    <button
                      onClick={() => void revoke(k.id)}
                      className="btn px-2.5 py-1.5 text-xs text-ink hover:bg-ink/[0.04]"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {keys.length === 0 && (
          <EmptyState
            icon={KeyRound}
            title="No API keys yet"
            hint="Create one above to call the API."
          />
        )}
      </Card>
    </div>
  );
}
